import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useAuth } from '../lib/auth.js';
import { api, ApiError, fetchCompanyOptions, fetchCompetentPersonOptions } from '../lib/api.js';
import { generateForm10ClientPDF } from '../lib/pdf-form10-client.js';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useFormFields } from '../hooks/useFormFields.js';
import { AsyncSearchableSelect } from '../components/AsyncSearchableSelect.js';
import { SearchableSelect } from '../components/SearchableSelect.js';
import { Pagination } from '../components/Pagination';
import { draftManager } from '../lib/draftManager.js';
import { useDraftAutoSave } from '../hooks/useDraftAutoSave.js';
import { DraftRecoveryBanner } from '../components/DraftRecoveryBanner.js';
import { SectionDraftsView } from '../components/SectionDraftsView.js';
import type { UserDraft } from '@gng-ehs/shared';
import { TableLoader } from '../components/TableLoader';
import { CompanyHoverBadge } from '../components/CompanyHoverBadge';
import { SortableHeader } from '../components/SortableHeader';
import { TimestampCell } from '../components/TimestampCell';
import { formatDateOnly, formatDate } from '../lib/dateUtils';
import { InlinePersonnelForm } from '../components/InlinePersonnelForm.js';
import { InlineEquipmentTypeForm } from '../components/InlineEquipmentTypeForm.js';
import { InlineCompanyForm } from '../components/InlineCompanyForm.js';
import { InlineTestMethodForm } from '../components/InlineTestMethodForm.js';
import { useDebounce } from '../hooks/useDebounce';
import { generateSmartShortCodeCandidates, checkLicenceStatus, validateMultipleEmails, formatMultipleEmails, validateOptionalMultipleEmails } from '@gng-ehs/shared';
import { Form10ExcelImportModal } from '../components/Form10ExcelImportModal.js';
import { Form10ExportIndexModal } from '../components/Form10ExportIndexModal.js';
import { useBulkSelection } from '../hooks/useBulkSelection';
import { BulkActionBar } from '../components/BulkActionBar';
import {
  getEquipmentRule,
  calculateTestLoad,
  buildPoint5Sentence,
  buildEquipmentTypeOptions,
  buildTestMethodOptions,
  DEFAULT_TEST_METHOD,
} from '@gng-ehs/shared/equipment-rules';

interface Form10 {
  id: string;
  certificate_number: string;
  company_id?: string;
  equipment_id?: string;
  personnel_id?: string;
  company_name?: string;
  company_short_code?: string;
  internal_id_number?: string;
  equipment_short_code?: string;
  inspector_name?: string;
  examination_date: string;
  next_inspection_date: string;
  test_method?: string;
  defect_status: string;
  defect_remarks?: string;
  annealing_details?: string;
  previous_inspection_details?: string;
  created_at?: string;
  updated_at?: string;
}

interface Company {
  id: string;
  short_code: string;
  company_name: string;
  occupier_name: string;
  factory_address: string;
  license_number?: string;
  license_expiry_date?: string;
  registration_number?: string;
  nic_code?: string;
  contact_email?: string;
  communication_email?: string;
  contact_person?: string;
  mobile_number?: string;
  created_at?: string;
  updated_at?: string;
}

interface Equipment {
  id: string;
  company_id: string;
  internal_id_number: string;
  equipment_short_code: string;
  equipment_name_type: string;
  swl_value?: number;
  swl_text?: string;
  lift_height?: string;
  make?: string;
  model?: string;
  equipment_size?: string;
  location_in_factory?: string;
  first_use_value?: string;
  company_name?: string;
}

interface Personnel {
  id: string;
  full_name: string;
  competency_cert_number?: string;
  competency_valid_upto?: string;
  issuing_authority?: string;
}

const yearOptions = Array.from({ length: 60 }, (_, i) => String(new Date().getFullYear() - i));

export function Form10Page(): React.ReactNode {
  const { canWrite } = useAuth();
  const isWrite = canWrite('form10');

  const [items, setItems] = useState<Form10[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [excelImportModalOpen, setExcelImportModalOpen] = useState(false);
  const [exportIndexModalOpen, setExportIndexModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const {
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    isSelected,
    isAllSelected,
    selectedCount,
    masterCheckboxRef,
  } = useBulkSelection(items);



  // PDF View Modal state for existing certificate
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewCertId, setViewCertId] = useState<string | null>(null);
  const [viewCertNumber, setViewCertNumber] = useState<string>('');
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  const openViewModal = async (f: Form10) => {
    setViewCertId(f.id);
    setViewCertNumber(f.certificate_number);
    setViewLoading(true);
    setViewModalOpen(true);
    try {
      const url = await api.form10.getPdfBlobUrl(f.id);
      setPdfBlobUrl(url);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch certificate PDF');
      setViewModalOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setViewCertId(null);
  };

  const fields = useFormFields({
    company_id: '',
    equipment_id: '',
    personnel_id: '',
    examination_date: new Date().toISOString().split('T')[0],
    test_method: DEFAULT_TEST_METHOD,
    defect_status: 'SAFE',
    defect_remarks: '',
    annealing_details: 'Not Applicable',
    previous_inspection_details: 'Not Applicable',
  });

  // Inline company creation
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [showEditCompanyForm, setShowEditCompanyForm] = useState(false);

  // Form 10 Editing state
  const [editing, setEditing] = useState<Form10 | null>(null);

  // Inline equipment creation & Mode Switch (Create New vs Clone Existing)
  const [equipMode, setEquipMode] = useState<'NEW' | 'CLONE'>('NEW');
  const [cloneSourceEquipId, setCloneSourceEquipId] = useState('');
  const equipFields = useFormFields({
    company_id: '', equipment_name_type: '', equipment_short_code: '',
    swl_value: '', lift_height: '', make: '', model: '',
    equipment_size: '', location_in_factory: '',
    first_use_type: 'NOT_APPLICABLE', first_use_value: '',
  });

  const [customTypes, setCustomTypes] = useState<Array<{ key: string; label: string; short_code?: string }>>([]);
  const [showInlineTypeForm, setShowInlineTypeForm] = useState(false);

  const [customTestMethods, setCustomTestMethods] = useState<Array<{ id: string; name: string }>>([]);
  const [showInlineTestMethodForm, setShowInlineTestMethodForm] = useState(false);

  useEffect(() => {
    api.equipmentTypes.list()
      .then((res) => {
        if (res.data) setCustomTypes(res.data.map((t) => ({ key: t.key, label: t.label, short_code: t.short_code })));
      })
      .catch(() => { /* ignore */ });

    api.testMethods.list()
      .then((res) => {
        if (res.data) setCustomTestMethods(res.data);
      })
      .catch(() => { /* ignore */ });
  }, []);

  const allTypeOptions = useMemo(() => {
    return buildEquipmentTypeOptions(customTypes);
  }, [customTypes]);

  const allTestMethodOptions = useMemo(() => {
    return buildTestMethodOptions(customTestMethods);
  }, [customTestMethods]);

  const equipRule = useMemo(
    () => getEquipmentRule(equipFields.values.equipment_name_type),
    [equipFields.values.equipment_name_type],
  );

  const equipPreviewLoad = useMemo(() => {
    if (!equipRule) return null;
    return calculateTestLoad(equipRule, parseFloat(equipFields.values.swl_value) || null);
  }, [equipRule, equipFields.values.swl_value]);

  const equipPreviewSentence = useMemo(() => {
    if (!equipRule) return '';
    const swl = parseFloat(equipFields.values.swl_value) || null;
    return buildPoint5Sentence(equipRule, swl, 'DD-MM-YYYY', '[Inspector]', fields.values.test_method || DEFAULT_TEST_METHOD);
  }, [equipRule, equipFields.values.swl_value, fields.values.test_method]);

  useEffect(() => {
    equipFields.setField('company_id', fields.values.company_id);
  }, [fields.values.company_id]);

  const [equipTagStatus, setEquipTagStatus] = useState<{ loading: boolean; available?: boolean; message?: string; suggested?: string }>({ loading: false });

  // Auto-suggest Tag Name when company or equipment type changes in inline form
  useEffect(() => {
    const targetCompanyId = equipFields.values.company_id || fields.values.company_id;
    const { equipment_name_type } = equipFields.values;
    if (!targetCompanyId || !equipment_name_type) return;

    api.equipment.suggestTagName(targetCompanyId, equipment_name_type)
      .then((res) => {
        if (res.data?.suggested) {
          equipFields.setField('equipment_short_code', res.data.suggested);
        }
      })
      .catch(() => { /* ignore */ });
  }, [fields.values.company_id, equipFields.values.company_id, equipFields.values.equipment_name_type]);

  // Live Tag Name uniqueness validation for inline equipment form
  const debouncedEquipTagName = equipFields.values.equipment_short_code;
  useDebounce(debouncedEquipTagName, () => {
    const targetCompanyId = equipFields.values.company_id || fields.values.company_id;
    const { equipment_short_code } = equipFields.values;
    if (!targetCompanyId || !equipment_short_code.trim()) {
      setEquipTagStatus({ loading: false });
      return;
    }
    setEquipTagStatus({ loading: true });
    api.equipment.checkTagName(targetCompanyId, equipment_short_code.trim())
      .then((res) => {
        if (res.data) {
          setEquipTagStatus({
            loading: false,
            available: res.data.available,
            message: res.data.message,
            suggested: res.data.suggested,
          });
        }
      })
      .catch(() => setEquipTagStatus({ loading: false }))
      .finally(() => {
        setEquipTagStatus((prev) => ({ ...prev, loading: false }));
      });
  });

  // Explicit Suggest Tag Name button click handler for inline equipment
  const handleSuggestEquipTagName = async () => {
    const companyId = equipFields.values.company_id || fields.values.company_id;
    if (!companyId) {
      setModalError('Please select a Company first.');
      setEquipTagStatus({ loading: false });
      return;
    }
    if (!equipFields.values.equipment_name_type) {
      setModalError('Please select an Equipment Type first.');
      setEquipTagStatus({ loading: false });
      return;
    }
    setEquipTagStatus({ loading: true });
    try {
      const res = await api.equipment.suggestTagName(companyId, equipFields.values.equipment_name_type);
      if (res.data?.suggested) {
        equipFields.setField('equipment_short_code', res.data.suggested);
      }
    } catch {
      /* ignore */
    } finally {
      setEquipTagStatus((prev) => ({ ...prev, loading: false }));
    }
  };



  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [personnelFilter, setPersonnelFilter] = useState('');
  const [defectFilter, setDefectFilter] = useState('');
  const [fyFilter, setFyFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder(field === 'created_at' || field === 'examination_date' || field === 'next_inspection_date' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  // Inline personnel creation state
  const [showPersonnelForm, setShowPersonnelForm] = useState(false);

  const load = () => {
    setLoading(true);
    api.form10.list({
      page,
      limit: 20,
      search: search || undefined,
      company_id: companyFilter || undefined,
      personnel_id: personnelFilter || undefined,
      defect_status: defectFilter || undefined,
      financial_year: fyFilter || undefined,
      sortBy,
      sortOrder,
    })
      .then((r) => {
        setItems(r.data as Form10[] ?? []);
        setTotalPages((r as unknown as Record<string, number>).totalPages ?? 1);
        setTotal((r as unknown as Record<string, number>).total ?? 0);
      })
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page, companyFilter, personnelFilter, defectFilter, fyFilter, sortBy, sortOrder]);
  useDebounce(search, () => { setPage(1); load(); });

  // State for relations preview
  const [companyEquipment, setCompanyEquipment] = useState<Equipment[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedPersonnel, setSelectedPersonnel] = useState<Personnel | null>(null);
  const [modalError, setModalError] = useState('');

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'records' | 'drafts'>('records');
  const [draftCount, setDraftCount] = useState<number>(() => draftManager.listDrafts('form10').length);
  const [isResumed, setIsResumed] = useState(false);
  const handledResumeRef = useRef<string | null>(null);

  useEffect(() => {
    const update = () => setDraftCount(draftManager.listDrafts('form10').length);
    update();
    const unsub = draftManager.subscribe(update);
    return unsub;
  }, []);

  const restoreForm10Draft = useCallback((draft: UserDraft) => {
    const data = draft.form_data as Record<string, unknown>;
    setEditing(null);
    setModalError('');
    if (data.equipMode) setEquipMode(data.equipMode as 'NEW' | 'CLONE');
    if (data.cloneSourceEquipId) setCloneSourceEquipId(String(data.cloneSourceEquipId));
    if (data.fields && typeof data.fields === 'object') {
      fields.patchValues(data.fields as any);
    }
    if (data.equipFields && typeof data.equipFields === 'object') {
      equipFields.patchValues(data.equipFields as any);
    }
    setModalOpen(true);
  }, []);

  const handleResumeDraft = (draft: UserDraft) => {
    setIsResumed(true);
    restoreForm10Draft(draft);
  };

  // Check URL search params for ?resumeDraft=...
  useEffect(() => {
    const resumeId = searchParams.get('resumeDraft');
    if (resumeId && handledResumeRef.current !== resumeId) {
      handledResumeRef.current = resumeId;
      const draft = draftManager.getDraft(resumeId);
      if (draft && draft.entity_type === 'form10') {
        setIsResumed(true);
        restoreForm10Draft(draft);
      }
      const next = new URLSearchParams(searchParams);
      next.delete('resumeDraft');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, restoreForm10Draft]);

  const isForm10Dirty = useMemo(() => {
    if (!modalOpen || editing) return false;
    return Boolean(
      fields.values.company_id ||
      fields.values.equipment_id ||
      fields.values.personnel_id ||
      equipFields.values.equipment_name_type ||
      equipFields.values.swl_value
    );
  }, [modalOpen, editing, fields.values, equipFields.values]);

  const draftTitle = useMemo(() => {
    const comp = selectedCompany?.company_name || 'Form 10 Inspection';
    const equip = equipRule?.label || equipFields.values.equipment_name_type || '';
    return equip ? `${comp} — ${equip}` : comp;
  }, [selectedCompany, equipRule, equipFields.values.equipment_name_type]);

  const draftFormData = useMemo(() => ({
    fields: fields.values,
    equipFields: equipFields.values,
    equipMode,
    cloneSourceEquipId,
  }), [fields.values, equipFields.values, equipMode, cloneSourceEquipId]);

  const { existingDraft, restoreDraft, discardDraft, clearDraft } = useDraftAutoSave({
    entityType: 'form10',
    draftId: 'dft_form10_new',
    enabled: modalOpen && !editing,
    isDirty: isForm10Dirty,
    isResumed,
    formData: draftFormData,
    title: draftTitle,
    subtitle: fields.values.examination_date ? `Exam Date: ${formatDateOnly(fields.values.examination_date)}` : undefined,
  });

  // Fetch company details
  useEffect(() => {
    fields.setField('equipment_id', '');
    if (fields.values.company_id) {
      api.companies.get(fields.values.company_id)
        .then((r) => setSelectedCompany((r as unknown as { data: Company }).data))
        .catch(() => setSelectedCompany(null));
      api.equipment.list({ company_id: fields.values.company_id, limit: 200 })
        .then((r) => setCompanyEquipment((r.data as Equipment[]) ?? []))
        .catch(() => setCompanyEquipment([]));
    } else {
      setSelectedCompany(null);
      setCompanyEquipment([]);
    }
  }, [fields.values.company_id]);

  const [personnelSigDataUrl, setPersonnelSigDataUrl] = useState<string | null>(null);
  const [personnelStampDataUrl, setPersonnelStampDataUrl] = useState<string | null>(null);

  // Fetch personnel details & images
  useEffect(() => {
    if (fields.values.personnel_id) {
      api.personnel.get(fields.values.personnel_id)
        .then((r) => setSelectedPersonnel((r as unknown as { data: Personnel }).data))
        .catch(() => setSelectedPersonnel(null));

      api.personnel.getImageDataUrl(fields.values.personnel_id, 'signature')
        .then((dataUrl) => setPersonnelSigDataUrl(dataUrl))
        .catch(() => setPersonnelSigDataUrl(null));

      api.personnel.getImageDataUrl(fields.values.personnel_id, 'stamp')
        .then((dataUrl) => setPersonnelStampDataUrl(dataUrl))
        .catch(() => setPersonnelStampDataUrl(null));
    } else {
      setSelectedPersonnel(null);
      setPersonnelSigDataUrl(null);
      setPersonnelStampDataUrl(null);
    }
  }, [fields.values.personnel_id]);

  // Auto-fetch prior inspection details when equipment_id or examination_date changes
  useEffect(() => {
    if (fields.values.equipment_id) {
      api.form10.getPreviousInspection(fields.values.equipment_id, fields.values.examination_date)
        .then((r) => {
          if (r.data?.previous_inspection_details) {
            fields.setField('previous_inspection_details', r.data.previous_inspection_details);
          }
        })
        .catch(() => {});
    } else {
      fields.setField('previous_inspection_details', 'Not Applicable');
    }
  }, [fields.values.equipment_id, fields.values.examination_date]);

  // Selected Personnel licence validity status
  const personnelLicenceStatus = useMemo(() => {
    return checkLicenceStatus(selectedPersonnel?.competency_valid_upto);
  }, [selectedPersonnel?.competency_valid_upto]);

  useEffect(() => { load(); }, [fyFilter]);

  // Selected Equipment object (if editing existing Form 10)
  const selectedEquip = useMemo(
    () => companyEquipment.find((e) => e.id === fields.values.equipment_id),
    [companyEquipment, fields.values.equipment_id],
  );

  // Effective Live Equipment Object (combines inline equipment fields when creating or selectedEquip when editing)
  const effectiveEquip = useMemo(() => {
    if (editing && selectedEquip) return selectedEquip;

    const typeKey = equipFields.values.equipment_name_type;
    const swlVal = equipFields.values.swl_value ? parseFloat(equipFields.values.swl_value) : null;

    return {
      id: 'live_preview_equip',
      company_id: fields.values.company_id,
      equipment_short_code: equipFields.values.equipment_short_code || 'TAG-01',
      internal_id_number: '01',
      equipment_name_type: typeKey || 'BOPT',
      swl_value: swlVal,
      lift_height: equipFields.values.lift_height || null,
      make: equipFields.values.make || null,
      model: equipFields.values.model || null,
      equipment_size: equipFields.values.equipment_size || null,
      location_in_factory: equipFields.values.location_in_factory || null,
      first_use_type: equipFields.values.first_use_type || 'NOT_APPLICABLE',
      first_use_value: equipFields.values.first_use_value || null,
    } as unknown as Equipment;
  }, [editing, selectedEquip, fields.values.company_id, equipFields.values]);

  // Live Equipment Rule
  const liveRule = useMemo(
    () => (effectiveEquip ? getEquipmentRule(effectiveEquip.equipment_name_type) : null),
    [effectiveEquip],
  );

  // Live Point 5 sentence
  const livePoint5 = useMemo(() => {
    const swl = effectiveEquip?.swl_value ?? null;
    const examDateStr = fields.values.examination_date
      ? formatDate(fields.values.examination_date)
      : 'DD-MM-YYYY';
    const inspectorName = selectedPersonnel?.full_name ?? '[Inspector]';
    const method = fields.values.test_method || DEFAULT_TEST_METHOD;
    return buildPoint5Sentence(liveRule ?? undefined, swl, examDateStr, inspectorName, method);
  }, [liveRule, effectiveEquip, fields.values.examination_date, fields.values.test_method, selectedPersonnel]);

  // Calculated next inspection date string
  const liveNextInspDate = useMemo(() => {
    if (!fields.values.examination_date) return 'DD-MM-YYYY';
    try {
      const [y, m, d] = fields.values.examination_date.split('-').map(Number);
      const dt = new Date(y, m - 1, d);
      dt.setFullYear(dt.getFullYear() + 1);
      dt.setDate(dt.getDate() - 1);
      return `${String(dt.getDate()).padStart(2, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${dt.getFullYear()}`;
    } catch {
      return 'DD-MM-YYYY';
    }
  }, [fields.values.examination_date]);

  // Live PDF Blob URL for client-side live PDF iframe preview
  const livePdfBlobUrl = useMemo(() => {
    try {
      return generateForm10ClientPDF({
        certificate_number: `G&G/${selectedCompany?.short_code || 'CO'}/${new Date().getFullYear().toString().slice(-2)}/XX`,
        examination_date: fields.values.examination_date,
        company: selectedCompany || undefined,
        equipment: effectiveEquip || undefined,
        personnel: selectedPersonnel || undefined,
        personnel_signature_data_url: personnelSigDataUrl,
        personnel_stamp_data_url: personnelStampDataUrl,
        test_prefix_sentence: livePoint5,
        previous_inspection_details: fields.values.previous_inspection_details,
        annealing_details: fields.values.annealing_details,
        defect_status: fields.values.defect_status,
        defect_remarks: (fields.values.defect_status === 'SAFE' || fields.values.defect_status === 'NOT_APPLICABLE') ? undefined : fields.values.defect_remarks,
        next_inspection_date: liveNextInspDate,
      });
    } catch {
      return null;
    }
  }, [fields.values, selectedCompany, effectiveEquip, selectedPersonnel, personnelSigDataUrl, personnelStampDataUrl, livePoint5, liveNextInspDate]);

  const isCompanyIncomplete = useMemo(() => {
    if (!selectedCompany) return false;
    const c = selectedCompany;
    const email = c.contact_email?.trim() ?? '';
    const emails = email.split(/[,;\s]+/).filter(Boolean);
    const isEmailDummy = emails.length === 0 || emails.every((e) => e === 'info@company.com' || e === 'contact@company.com');
    return (
      !c.company_name?.trim() ||
      !c.short_code?.trim() ||
      !c.occupier_name?.trim() || c.occupier_name.trim() === 'N/A' ||
      !c.factory_address?.trim() || c.factory_address.trim() === 'N/A' ||
      !c.license_number?.trim() ||
      !c.registration_number?.trim() ||
      isEmailDummy
    );
  }, [selectedCompany]);

  const editCompanyFields = useFormFields({
    short_code: '', company_name: '', occupier_name: '',
    factory_address: '', license_number: '', license_expiry_date: '', registration_number: '',
    nic_code: '', contact_email: '', communication_email: '', contact_person: '', mobile_number: '',
  });

  const [updatingCompany, setUpdatingCompany] = useState(false);

  useEffect(() => {
    if (selectedCompany) {
      const email = selectedCompany.contact_email?.trim() ?? '';
      const cleanEmail = (email === 'info@company.com' || email === 'contact@company.com') ? '' : email;
      editCompanyFields.patchValues({
        short_code: selectedCompany.short_code ?? '',
        company_name: selectedCompany.company_name ?? '',
        occupier_name: selectedCompany.occupier_name === 'N/A' ? '' : (selectedCompany.occupier_name ?? ''),
        factory_address: selectedCompany.factory_address === 'N/A' ? '' : (selectedCompany.factory_address ?? ''),
        license_number: selectedCompany.license_number ?? '',
        license_expiry_date: selectedCompany.license_expiry_date ?? '',
        registration_number: selectedCompany.registration_number ?? '',
        nic_code: selectedCompany.nic_code ?? '',
        contact_email: cleanEmail,
        communication_email: selectedCompany.communication_email ?? '',
        contact_person: selectedCompany.contact_person ?? '',
        mobile_number: selectedCompany.mobile_number ?? '',
      });
    }
  }, [selectedCompany]);

  const [checkingEditShortCode, setCheckingEditShortCode] = useState(false);
  const [editShortCodeStatus, setEditShortCodeStatus] = useState<{ available?: boolean; message?: string; suggested?: string } | null>(null);

  useDebounce(editCompanyFields.values.short_code, async () => {
    const code = editCompanyFields.values.short_code.trim();
    if (!code || !selectedCompany) {
      setEditShortCodeStatus(null);
      return;
    }
    setCheckingEditShortCode(true);
    try {
      const res = await api.companies.checkShortCode(code, editCompanyFields.values.company_name, selectedCompany.id);
      if (res.data) setEditShortCodeStatus(res.data);
    } catch {
      setEditShortCodeStatus(null);
    } finally {
      setCheckingEditShortCode(false);
    }
  }, 350);

  const isEditCompanyValid = useMemo(() => {
    const v = editCompanyFields.values;
    return (
      !!v.company_name.trim() &&
      !!v.short_code.trim() &&
      !!v.occupier_name.trim() && v.occupier_name.trim() !== 'N/A' &&
      !!v.factory_address.trim() && v.factory_address.trim() !== 'N/A' &&
      !!v.license_number.trim() &&
      !!v.registration_number.trim() &&
      validateMultipleEmails(v.contact_email) &&
      validateOptionalMultipleEmails(v.communication_email) &&
      (editShortCodeStatus?.available !== false)
    );
  }, [editCompanyFields.values, editShortCodeStatus]);

  const isForm10Valid = useMemo(() => {
    if (!fields.values.company_id || !fields.values.personnel_id || !fields.values.examination_date) {
      return false;
    }
    if (personnelLicenceStatus.isExpired) {
      return false;
    }
    if (!editing) {
      if (!equipFields.values.equipment_name_type?.trim() || !equipFields.values.equipment_short_code?.trim()) {
        return false;
      }
      if (equipTagStatus.available === false) return false;
    } else {
      if (!fields.values.equipment_id) return false;
    }
    return true;
  }, [fields.values, editing, equipFields.values, equipTagStatus, personnelLicenceStatus.isExpired]);

  const handleSuggestEditShortCode = async () => {
    const name = editCompanyFields.values.company_name.trim();
    if (!name) return;
    try {
      const res = await api.companies.suggestShortCode(name, selectedCompany?.id);
      if (res.data?.short_code) editCompanyFields.setField('short_code', res.data.short_code);
    } catch {
      const candidates = generateSmartShortCodeCandidates(name);
      if (candidates.length > 0) editCompanyFields.setField('short_code', candidates[0]);
    }
  };

  const handleUpdateCompanyDetails = async () => {
    if (!selectedCompany) return;
    const missing: string[] = [];
    if (!editCompanyFields.values.company_name.trim()) missing.push('Company Name');
    if (!editCompanyFields.values.short_code.trim()) missing.push('Short Code');
    if (!editCompanyFields.values.occupier_name.trim()) missing.push('Occupier Name');
    if (!editCompanyFields.values.factory_address.trim()) missing.push('Factory Address');
    if (!editCompanyFields.values.license_number.trim()) missing.push('License Number');
    if (!editCompanyFields.values.registration_number.trim()) missing.push('Registration Number');
    if (!editCompanyFields.values.contact_email.trim()) missing.push('Contact Email');

    if (missing.length) {
      setModalError(`Please fill in all mandatory company fields: ${missing.join(', ')}.`);
      return;
    }

    const emailVal = editCompanyFields.values.contact_email.trim();
    if (!validateMultipleEmails(emailVal)) {
      setModalError('Please enter valid Contact Email address(es), separated by commas.');
      return;
    }

    if (!validateOptionalMultipleEmails(editCompanyFields.values.communication_email)) {
      setModalError('Please enter valid Communication Email address(es), separated by commas.');
      return;
    }

    if (editShortCodeStatus && !editShortCodeStatus.available) {
      setModalError(`Cannot save: Short code '${editCompanyFields.values.short_code.toUpperCase()}' is already taken by another company. Please enter a unique short code or click 'Suggest'.`);
      return;
    }

    setUpdatingCompany(true);
    try {
      const payload = {
        ...editCompanyFields.values,
        contact_email: formatMultipleEmails(emailVal) || '',
        communication_email: formatMultipleEmails(editCompanyFields.values.communication_email) || '',
      };
      const updatedRes = await api.companies.update(selectedCompany.id, payload);
      const updatedComp = (updatedRes as { data?: Company }).data;
      if (updatedComp) {
        setSelectedCompany(updatedComp);
      }
      setModalError('');
    } catch (err: unknown) {
      setModalError(err instanceof Error ? err.message : 'Failed to update company details');
    } finally {
      setUpdatingCompany(false);
    }
  };

  const openCreate = () => {
    setIsResumed(false);
    setActiveTab('records');
    setEditing(null);
    setEquipMode('NEW');
    setCloneSourceEquipId('');
    fields.reset({
      company_id: '',
      equipment_id: '',
      personnel_id: '',
      examination_date: new Date().toISOString().split('T')[0],
      test_method: DEFAULT_TEST_METHOD,
      defect_status: 'SAFE',
      defect_remarks: '',
      annealing_details: 'Not Applicable',
      previous_inspection_details: 'Not Applicable',
    });
    equipFields.reset({
      company_id: '',
      equipment_name_type: '',
      equipment_short_code: '',
      swl_value: '',
      lift_height: '',
      make: '',
      model: '',
      equipment_size: '',
      location_in_factory: '',
      first_use_type: 'NOT_APPLICABLE',
      first_use_value: '',
    });
    setModalError('');
    setModalOpen(true);
  };

  const openEdit = (f: Form10) => {
    setIsResumed(false);
    setEditing(f);
    fields.patchValues({
      company_id: f.company_id ?? '',
      equipment_id: f.equipment_id ?? '',
      personnel_id: f.personnel_id ?? '',
      examination_date: f.examination_date ?? new Date().toISOString().split('T')[0],
      test_method: f.test_method ?? DEFAULT_TEST_METHOD,
      defect_status: f.defect_status ?? 'SAFE',
      defect_remarks: f.defect_remarks ?? '',
      annealing_details: f.annealing_details ?? 'Not Applicable',
      previous_inspection_details: f.previous_inspection_details ?? 'Not Applicable',
    });
    setModalError('');
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isCompanyIncomplete) {
      setModalError('Selected company is missing mandatory details. Please complete and save company details below before creating Form 10.');
      return;
    }

    if (!fields.values.company_id) {
      setModalError('Please select a Company first.');
      return;
    }

    if (personnelLicenceStatus.isExpired) {
      setModalError(`Selected Competent Person (${selectedPersonnel?.full_name || 'Inspector'}) has an expired competency licence (expired on ${personnelLicenceStatus.formattedDate}). Cannot create or update Form 10.`);
      return;
    }

    setSaving(true);
    try {
      const form10Payload = {
        ...fields.values,
        defect_remarks: (fields.values.defect_status === 'SAFE' || fields.values.defect_status === 'NOT_APPLICABLE') ? undefined : (fields.values.defect_remarks?.trim() || undefined),
      };

      if (editing) {
        await api.form10.update(editing.id, form10Payload);
      } else {
        // Step 1: Validate Equipment Fields
        const finalTagName = equipFields.values.equipment_short_code.trim().toUpperCase();
        const missing: string[] = [];
        if (!equipFields.values.equipment_name_type) missing.push('Equipment Type');
        if (!finalTagName) missing.push('Tag Name');
        if (
          equipFields.values.first_use_type !== 'NOT_APPLICABLE' &&
          equipFields.values.first_use_type !== 'NOT_KNOWN' &&
          !equipFields.values.first_use_value
        ) {
          missing.push('First Use Value');
        }

        if (missing.length) {
          setModalError(`Please fill in mandatory equipment fields: ${missing.join(', ')}.`);
          setSaving(false);
          return;
        }

        if (equipTagStatus.available === false) {
          setModalError(equipTagStatus.message || `Tag Name '${finalTagName}' is already taken for this company.`);
          setSaving(false);
          return;
        }

        // Step 2: Create New Equipment Record
        const equipPayload = {
          ...equipFields.values,
          company_id: fields.values.company_id,
          equipment_short_code: finalTagName,
          first_use_value:
            equipFields.values.first_use_type === 'NOT_APPLICABLE' ||
            equipFields.values.first_use_type === 'NOT_KNOWN'
              ? equipFields.values.first_use_type === 'NOT_APPLICABLE'
                ? 'Not Applicable'
                : 'Not Known'
              : equipFields.values.first_use_value,
          swl_value: equipFields.values.swl_value ? parseFloat(equipFields.values.swl_value) : undefined,
        };

        const equipRes = await api.equipment.create(equipPayload);
        const createdEquip = (equipRes as { data?: Equipment }).data;
        if (!createdEquip || !createdEquip.id) {
          throw new Error('Failed to create equipment item.');
        }

        // Step 3: Create Form 10 Certificate linked to created equipment
        await api.form10.create({
          ...form10Payload,
          equipment_id: createdEquip.id,
        });
      }
      clearDraft();
      setModalOpen(false);
      setModalError('');
      load();
    } catch (err: unknown) {
      setModalError(err instanceof Error ? err.message : 'Failed to save certificate');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.form10.delete(deleteId);
      setSuccess('Certificate moved to Trash.');
      load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    setError('');
    try {
      const res = await api.form10.bulkDelete(Array.from(selectedIds));
      setSuccess(`Moved ${res.data?.count ?? selectedIds.size} Form 10 certificates to Trash.`);
      clearSelection();
      setBulkDeleteDialogOpen(false);
      load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete selected certificates');
    } finally {
      setBulkDeleting(false);
    }
  };

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(''), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  const downloadPdf = async (id: string) => {
    try { await api.form10.downloadPdf(id); }
    catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed to download PDF'); }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 space-y-4 overflow-hidden pt-1 px-1">
      {/* Action Toolbar */}
      <div className="shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Tab Pill Toggle */}
          <div className="flex items-center p-0.5 bg-gray-200/80 rounded-lg border border-gray-300/70">
            <button
              type="button"
              onClick={() => setActiveTab('records')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                activeTab === 'records'
                  ? 'bg-white text-gray-900 shadow-xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Form 10 Records
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('drafts')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer ${
                activeTab === 'drafts'
                  ? 'bg-white text-amber-900 shadow-xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span>Drafts</span>
              {draftCount > 0 && (
                <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-1.5 py-0.2 rounded-full border border-amber-200">
                  {draftCount}
                </span>
              )}
            </button>
          </div>

          {activeTab === 'records' && (
            <button onClick={load} className="btn btn-secondary">Refresh</button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={() => setExportIndexModalOpen(true)}
            className="btn bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 font-medium px-3 text-xs flex items-center gap-1.5 cursor-pointer shadow-xs transition-colors"
            title="Export Form 10 Lifting Equipment Index (PDF / Excel)"
          >
            <svg className="w-3.5 h-3.5 text-blue-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeLinecap="round" strokeLinejoin="round" />
              <polyline points="14 2 14 8 20 8" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="16" y1="13" x2="8" y2="13" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="16" y1="17" x2="8" y2="17" strokeLinecap="round" strokeLinejoin="round" />
              <polyline points="10 9 9 9 8 9" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Export Index</span>
          </button>
          {isWrite && (
            <>
              <button
                onClick={() => setExcelImportModalOpen(true)}
                className="btn bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-3 text-xs flex items-center gap-1.5 cursor-pointer shadow-sm"
              >
                <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>Bulk Excel Import</span>
              </button>
              <button onClick={() => setBulkModalOpen(true)} className="btn bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-3 text-xs flex items-center gap-1.5 cursor-pointer">
                <span>⚡ Bulk Generate</span>
              </button>
              <button onClick={openCreate} className="btn btn-primary">+ New Form 10</button>
            </>
          )}
        </div>
      </div>

      {/* Search & Filters Bar */}
      {activeTab === 'records' && (
        <div className="shrink-0 bg-white p-3 rounded-xl border border-gray-200 shadow-sm flex flex-col lg:flex-row gap-3 items-center justify-between">
          <div className="flex flex-wrap gap-2 items-center w-full lg:w-auto flex-1 min-w-0">
            <div className="relative w-full sm:w-64">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search report no, tag, cert..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input pl-9 text-sm w-full"
              />
            </div>
            <div className="w-full sm:w-60">
              <AsyncSearchableSelect
                placeholder="Filter by Company..."
                fetchOptions={fetchCompanyOptions}
                value={companyFilter}
                onChange={(val) => setCompanyFilter(val)}
              />
            </div>
            <div className="w-full sm:w-56">
              <AsyncSearchableSelect
                placeholder="Competent Person..."
                fetchOptions={fetchCompetentPersonOptions}
                value={personnelFilter}
                onChange={(val) => setPersonnelFilter(val)}
              />
            </div>
            <div className="w-full sm:w-44">
              <select
                className="input text-sm w-full"
                value={defectFilter}
                onChange={(e) => setDefectFilter(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="safe">Safe / Fit to Use</option>
                <option value="defect">Has Defects</option>
              </select>
            </div>
            <div className="w-full sm:w-44">
              <select
                className="input text-sm w-full"
                value={fyFilter}
                onChange={(e) => setFyFilter(e.target.value)}
              >
                <option value="">All Financial Years</option>
                {fyOptions().map((fy) => (
                  <option key={fy} value={fy}>FY {fy}-{String(parseInt(fy) + 1).slice(-2)}</option>
                ))}
              </select>
            </div>
          </div>
          {(search || companyFilter || personnelFilter || defectFilter || fyFilter) && (
            <button
              onClick={() => { setSearch(''); setCompanyFilter(''); setPersonnelFilter(''); setDefectFilter(''); setFyFilter(''); }}
              className="text-xs text-brand-700 font-semibold hover:underline shrink-0 px-2 py-1 cursor-pointer"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}

      {success && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-xs font-semibold text-emerald-800 flex items-center justify-between shadow-xs">
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="text-emerald-600 hover:text-emerald-900 ml-2 font-bold cursor-pointer">×</button>
        </div>
      )}
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      {activeTab === 'drafts' ? (
        <div className="flex-1 min-h-0 overflow-y-auto pr-1">
          <SectionDraftsView
            entityType="form10"
            sectionTitle="Form 10"
            onResume={handleResumeDraft}
          />
        </div>
      ) : loading ? (
        <TableLoader />
      ) : (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden space-y-2">
          <div className="table-wrapper">
            <table className="table">
              <thead className="sticky top-0 z-10 bg-white">
                <tr>
                  <th className="w-10 px-3 py-3 text-center">
                    <input
                      type="checkbox"
                      ref={masterCheckboxRef}
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                      title="Select all"
                    />
                  </th>
                  <SortableHeader label="Report / Cert No." field="certificate_number" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Company" field="company_name" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Equipment Code / ID" field="equipment_name" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Competent Person" field="inspector_name" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Exam Date" field="examination_date" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Next Insp." field="next_inspection_date" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Created / Updated" field="created_at" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.length === 0 ? (
                  <tr><td colSpan={9} className="text-center text-gray-500 py-8">No Form 10 certificates found</td></tr>
                ) : items.map((f) => (
                  <tr key={f.id} className={isSelected(f.id) ? 'bg-brand-50/50 hover:bg-brand-50/70' : 'hover:bg-brand-50/40'}>
                    <td className="w-10 px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected(f.id)}
                        onChange={() => {}}
                        onClick={(e) => toggleSelect(f.id, e.shiftKey)}
                        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                      />
                    </td>
                    <td className="font-medium text-sm text-gray-900 whitespace-nowrap">
                      <button
                        onClick={() => openViewModal(f)}
                        className="font-mono text-brand-600 hover:text-brand-800 hover:underline font-semibold text-xs"
                      >
                        {f.certificate_number}
                      </button>
                    </td>
                    <td className="font-medium text-sm text-gray-900">
                      {f.company_name ? (
                        <div className="inline-flex items-center gap-1.5">
                          <span>{f.company_name}</span>
                          {f.company_short_code && (
                            <CompanyHoverBadge shortCode={f.company_short_code} companyId={f.company_id} />
                          )}
                        </div>
                      ) : '—'}
                    </td>
                    <td>
                      <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">
                        {f.equipment_short_code}/{f.internal_id_number}
                      </span>
                    </td>
                    <td className="text-gray-600 text-sm">{f.inspector_name || '—'}</td>
                    <td className="text-gray-500 text-sm whitespace-nowrap">{formatDateOnly(f.examination_date)}</td>
                    <td className="text-sm font-medium whitespace-nowrap">
                      <span className={isOverdue(f.next_inspection_date) ? 'text-red-600 font-bold' : 'text-emerald-700'}>
                        {formatDateOnly(f.next_inspection_date)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap">
                      <TimestampCell createdAt={f.created_at} updatedAt={f.updated_at} />
                    </td>
                    <td>
                      <div className="flex gap-1.5 items-center">
                        <button
                          onClick={() => openViewModal(f)}
                          title="View Form 10 PDF Certificate"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200/80 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>View</span>
                        </button>
                        <button
                          onClick={() => downloadPdf(f.id)}
                          title="Download Form 10 PDF"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200/80 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>PDF</span>
                        </button>
                        {isWrite && (
                          <>
                            <button
                              onClick={() => openEdit(f)}
                              title="Edit Form 10 Certificate"
                              className="p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 hover:text-amber-800 border border-amber-200/80 transition-colors cursor-pointer"
                            >
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                            <button
                              onClick={() => setDeleteId(f.id)}
                              title="Delete Form 10"
                              className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 border border-red-200/80 transition-colors cursor-pointer"
                            >
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="shrink-0 pt-1">
            <Pagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
          </div>
        </div>
      )}

      {/* Full-Screen Split View Modal */}
      <Modal open={modalOpen} onClose={() => { setModalOpen(false); setIsResumed(false); setModalError(''); }} title={editing ? "Edit Form 10 Certificate" : "New Form 10 Certificate"} size="full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-[calc(100vh-65px)] items-stretch">
          
          {/* Left Panel: Form Inputs */}
          <div className="lg:col-span-5 space-y-4 overflow-y-auto h-full pr-3 pb-6">
            <DraftRecoveryBanner
              draft={existingDraft}
              onRestore={() => {
                const data = restoreDraft();
                if (data && existingDraft) {
                  restoreForm10Draft({ ...existingDraft, form_data: data });
                }
              }}
              onDiscard={discardDraft}
            />

            {modalError && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
                <span>{modalError}</span>
                <button type="button" onClick={() => setModalError('')} className="text-red-500 hover:text-red-700">✕</button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Field
                    label="Company"
                    required
                    hint={
                      <button type="button" onClick={() => setShowCompanyForm((v) => !v)}
                        className="text-xs text-brand-600 hover:text-brand-700 font-medium inline-flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {showCompanyForm ? 'Choose existing' : 'New Company'}
                      </button>
                    }
                  >
                    <AsyncSearchableSelect
                      value={fields.values.company_id}
                      onChange={(v) => fields.setField('company_id', v)}
                      fetchOptions={fetchCompanyOptions}
                      placeholder="Search company…"
                    />
                  </Field>

                  {/* Inline Company Creation Form */}
                  {showCompanyForm && (
                    <InlineCompanyForm
                      onCreated={(created) => {
                        fields.setField('company_id', created.id);
                        setSelectedCompany(created as unknown as Company);
                        setShowCompanyForm(false);
                        api.equipment.list({ company_id: created.id }).then((res) => {
                          if (res.data) setCompanyEquipment(res.data as unknown as typeof companyEquipment);
                        });
                      }}
                      onCancel={() => setShowCompanyForm(false)}
                    />
                  )}

                  {/* In-Place Missing Company Details Card (Collapsible) - Directly Below Company Dropdown */}
                  {selectedCompany && isCompanyIncomplete && !showCompanyForm && (
                    <div className="mt-2.5 rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-amber-900 font-semibold text-xs">
                          <svg className="w-4 h-4 text-amber-600 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span>Note: Company details incomplete ({selectedCompany.company_name})</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowEditCompanyForm((v) => !v)}
                          className="text-xs font-semibold text-amber-800 hover:text-amber-900 underline cursor-pointer shrink-0 whitespace-nowrap inline-flex items-center gap-1"
                        >
                          {showEditCompanyForm ? 'Hide Form ✕' : <span className="whitespace-nowrap">Complete Details ✏️</span>}
                        </button>
                      </div>

                      {showEditCompanyForm && (
                        <div className="pt-2 border-t border-amber-200/80 space-y-3">
                          <p className="text-xs text-amber-800">
                            The selected company <strong>{selectedCompany.company_name}</strong> is missing mandatory information (License No, Reg No, Occupier Name, or Factory Address). Complete and save these details below before generating Form 10.
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                            <Field label="Short Code" required hint={<span className="text-[11px] text-brand-600 font-medium">Must be unique</span>}>
                              <div className="space-y-1">
                                <div className="flex gap-1.5">
                                  <input className="input text-xs bg-white font-mono uppercase" {...editCompanyFields.bind('short_code')} placeholder="Short Code" />
                                  <button
                                    type="button"
                                    onClick={handleSuggestEditShortCode}
                                    title="Suggest available unique short code"
                                    className="px-2 py-1 text-xs font-semibold bg-brand-50 text-brand-700 hover:bg-brand-100 border border-brand-200/80 rounded-lg shrink-0 cursor-pointer"
                                  >
                                    Suggest
                                  </button>
                                </div>
                                {checkingEditShortCode && (
                                  <p className="text-[11px] text-blue-600 font-medium flex items-center gap-1">
                                    <span className="animate-spin h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full" />
                                    Checking availability…
                                  </p>
                                )}
                                {!checkingEditShortCode && editShortCodeStatus && editCompanyFields.values.short_code.trim() && (
                                  editShortCodeStatus.available ? (
                                    <p className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1">
                                      ✓ Short code '{editCompanyFields.values.short_code.toUpperCase()}' is available
                                    </p>
                                  ) : (
                                    <div className="text-[11px] text-red-600 font-semibold space-y-0.5">
                                      <p>✕ {editShortCodeStatus.message}</p>
                                      {editShortCodeStatus.suggested && (
                                        <p>
                                          Suggested code:{' '}
                                          <button
                                            type="button"
                                            onClick={() => editCompanyFields.setField('short_code', editShortCodeStatus.suggested!)}
                                            className="text-brand-700 underline font-bold hover:text-brand-900 cursor-pointer"
                                          >
                                            Use '{editShortCodeStatus.suggested}'
                                          </button>
                                        </p>
                                      )}
                                    </div>
                                  )
                                )}
                              </div>
                            </Field>
                            <Field label="Company Name" required>
                              <input className="input text-xs bg-white" {...editCompanyFields.bind('company_name')} placeholder="Company Name" />
                            </Field>
                            <Field label="Occupier Name" required>
                              <input className="input text-xs bg-white" {...editCompanyFields.bind('occupier_name')} placeholder="Occupier Name" />
                            </Field>
                            <Field label="Factory Address" required>
                              <input className="input text-xs bg-white" {...editCompanyFields.bind('factory_address')} placeholder="Factory Address" />
                            </Field>
                            <Field label="License No" required>
                              <input className="input text-xs bg-white" {...editCompanyFields.bind('license_number')} placeholder="License No" />
                            </Field>
                            <Field label="Reg No" required>
                              <input className="input text-xs bg-white" {...editCompanyFields.bind('registration_number')} placeholder="Reg No" />
                            </Field>
                            <Field label="Contact Email" hint="Comma-separated for multiple" required>
                              <input className="input text-xs bg-white" type="text" {...editCompanyFields.bind('contact_email')} placeholder="e.g. contact@company.com, admin@company.com" autoComplete="off" required />
                            </Field>
                            <Field label="Communication Email" hint="Optional, comma-separated for multiple">
                              <input className="input text-xs bg-white" type="text" {...editCompanyFields.bind('communication_email')} placeholder="e.g. comms@company.com" autoComplete="off" />
                            </Field>
                          </div>
                          <div className="flex justify-end pt-1">
                            <button
                              type="button"
                              onClick={handleUpdateCompanyDetails}
                              disabled={updatingCompany || !isEditCompanyValid}
                              className="btn btn-primary text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {updatingCompany ? 'Saving Company Details…' : 'Save & Update Company Details'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

                {/* Equipment Section: Shown by default within Form 10 modal */}
                {!editing && (
                  <div className="rounded-xl border border-brand-200 bg-brand-50/50 p-4 space-y-4 shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-brand-200/80 pb-3">
                      <div>
                        <h4 className="text-xs font-bold uppercase tracking-wider text-brand-900 flex items-center gap-1.5">
                          <span>⚙️ Equipment Details</span>
                          <span className="text-[10px] text-brand-600 font-normal normal-case">(Creates equipment with Form 10)</span>
                        </h4>
                      </div>

                      {/* Equipment Mode Switch: Create New vs Clone Existing */}
                      <div className="inline-flex p-0.5 bg-slate-200/80 rounded-lg shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            setEquipMode('NEW');
                            setCloneSourceEquipId('');
                          }}
                          className={`px-3 py-1 text-xs font-bold rounded-md transition-all cursor-pointer whitespace-nowrap ${
                            equipMode === 'NEW'
                              ? 'bg-white text-brand-700 shadow-sm'
                              : 'text-slate-600 hover:text-slate-900'
                          }`}
                        >
                          ➕ Create New
                        </button>
                        <button
                          type="button"
                          onClick={() => setEquipMode('CLONE')}
                          className={`px-3 py-1 text-xs font-bold rounded-md transition-all cursor-pointer whitespace-nowrap ${
                            equipMode === 'CLONE'
                              ? 'bg-white text-indigo-700 shadow-sm'
                              : 'text-slate-600 hover:text-slate-900'
                          }`}
                        >
                          📋 Clone Existing
                        </button>
                      </div>
                    </div>

                    {/* Clone Selector in Clone Mode */}
                    {equipMode === 'CLONE' && (
                      <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200 space-y-2">
                        <Field label="Select Equipment to Clone" required hint={<span className="text-[11px] text-indigo-600">Prefills specs & auto-suggests new tag</span>}>
                          <SearchableSelect
                            options={companyEquipment.map((e) => ({
                              value: e.id,
                              label: `${e.equipment_short_code || e.internal_id_number} — ${e.equipment_name_type}${e.swl_value ? ` (${e.swl_value}T)` : ''}`,
                            }))}
                            value={cloneSourceEquipId}
                            onChange={(val) => {
                              setCloneSourceEquipId(val);
                              const src = companyEquipment.find((e) => e.id === val);
                              if (src) {
                                equipFields.patchValues({
                                  equipment_name_type: src.equipment_name_type,
                                  swl_value: src.swl_value ? String(src.swl_value) : '',
                                  lift_height: src.lift_height || '',
                                  make: src.make || '',
                                  model: src.model || '',
                                  equipment_size: src.equipment_size || '',
                                  location_in_factory: src.location_in_factory || '',
                                  first_use_type: ((src as unknown as Record<string, string>).first_use_type || (src as unknown as Record<string, string>).first_use_date_type || 'NOT_APPLICABLE'),
                                  first_use_value: ((src as unknown as Record<string, string>).first_use_value || (src as unknown as Record<string, string>).first_use_date_value || ''),
                                });
                                const compId = fields.values.company_id;
                                if (compId && src.equipment_name_type) {
                                  api.equipment.suggestTagName(compId, src.equipment_name_type)
                                    .then((res) => {
                                      if (res.data?.suggested) {
                                        equipFields.setField('equipment_short_code', res.data.suggested);
                                      }
                                    })
                                    .catch(() => {});
                                }
                              }
                            }}
                            placeholder={fields.values.company_id ? 'Select equipment to clone…' : 'Select a company above first'}
                            disabled={!fields.values.company_id}
                          />
                        </Field>
                      </div>
                    )}

                    {/* Inline Equipment Specification Fields */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Field
                        label="Equipment Type"
                        required
                        hint={
                          <button
                            type="button"
                            onClick={() => setShowInlineTypeForm(!showInlineTypeForm)}
                            className="text-[11px] text-brand-700 font-bold hover:underline cursor-pointer"
                          >
                            + New Type
                          </button>
                        }
                      >
                        <SearchableSelect
                          options={allTypeOptions}
                          value={equipFields.values.equipment_name_type}
                          onChange={(v) => {
                            equipFields.setField('equipment_name_type', v);
                            const compId = fields.values.company_id;
                            if (compId && v) {
                              api.equipment.suggestTagName(compId, v)
                                .then((res) => {
                                  if (res.data?.suggested) {
                                    equipFields.setField('equipment_short_code', res.data.suggested);
                                  }
                                })
                                .catch(() => {});
                            }
                          }}
                          placeholder="Select type…"
                        />
                        {showInlineTypeForm && (
                          <InlineEquipmentTypeForm
                            onCreated={(newType) => {
                              setCustomTypes((prev) => [...prev, newType]);
                              equipFields.setField('equipment_name_type', newType.key);
                              const compId = fields.values.company_id;
                              if (compId && newType.key) {
                                api.equipment.suggestTagName(compId, newType.key)
                                  .then((res) => {
                                    if (res.data?.suggested) {
                                      equipFields.setField('equipment_short_code', res.data.suggested);
                                    }
                                  })
                                  .catch(() => {});
                              }
                              setShowInlineTypeForm(false);
                            }}
                            onCancel={() => setShowInlineTypeForm(false)}
                          />
                        )}
                      </Field>

                      <Field
                        label="Tag Name (Equipment Code)"
                        required
                        hint={<span className="text-[11px] text-brand-600 font-medium">Click Suggest to auto-generate</span>}
                      >
                        <div className="space-y-1">
                          <div className="flex gap-2">
                            <input
                              className={`input font-mono uppercase font-semibold text-xs bg-white ${
                                equipTagStatus.available === false ? 'border-red-500 bg-red-50 focus:ring-red-500' : ''
                              }`}
                              {...equipFields.bind('equipment_short_code')}
                              placeholder="e.g. SPL/BOPT/01"
                              required
                            />
                            <button
                              type="button"
                              onClick={handleSuggestEquipTagName}
                              title="Suggest available unique Tag Name"
                              className="px-2.5 py-1 text-xs font-semibold bg-brand-50 text-brand-700 hover:bg-brand-100 border border-brand-200/80 rounded-lg shrink-0 cursor-pointer"
                            >
                              Suggest
                            </button>
                          </div>
                          {equipTagStatus.loading && (
                            <p className="text-xs text-blue-600 font-medium flex items-center gap-1.5 pt-0.5">
                              <span className="animate-spin h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full" />
                              Checking Tag Name availability…
                            </p>
                          )}
                          {!equipTagStatus.loading && equipTagStatus.available === true && equipFields.values.equipment_short_code.trim() && (
                            <p className="text-xs text-emerald-700 font-semibold flex items-center gap-1 pt-0.5">
                              ✓ Tag Name '{equipFields.values.equipment_short_code.toUpperCase()}' is available
                            </p>
                          )}
                          {!equipTagStatus.loading && equipTagStatus.available === false && equipFields.values.equipment_short_code.trim() && (
                            <div className="text-xs text-red-600 font-semibold space-y-0.5 pt-0.5">
                              <p>✕ {equipTagStatus.message || `Tag Name '${equipFields.values.equipment_short_code.toUpperCase()}' already exists`}</p>
                              {equipTagStatus.suggested && (
                                <p>
                                  Suggested Tag Name:{' '}
                                  <button
                                    type="button"
                                    onClick={() => equipFields.setField('equipment_short_code', equipTagStatus.suggested!)}
                                    className="text-brand-700 underline font-bold hover:text-brand-900 cursor-pointer"
                                  >
                                    Use '{equipTagStatus.suggested}'
                                  </button>
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </Field>

                      <Field label="SWL (Ton)">
                        <input className="input text-xs bg-white" type="number" step="0.1" {...equipFields.bind('swl_value')} placeholder="e.g. 2.0" />
                      </Field>
                      <Field label="Lift Height">
                        <input className="input text-xs bg-white" {...equipFields.bind('lift_height')} placeholder="e.g. 200 mm" />
                      </Field>
                      <Field label="Make">
                        <input className="input text-xs bg-white" {...equipFields.bind('make')} placeholder="e.g. Godrej" />
                      </Field>
                      <Field label="Model">
                        <input className="input text-xs bg-white" {...equipFields.bind('model')} placeholder="e.g. M-1" />
                      </Field>
                      <Field label="Size">
                        <input className="input text-xs bg-white" {...equipFields.bind('equipment_size')} placeholder="e.g. 560 x 1150 mm" />
                      </Field>
                      <Field label="Location in Factory">
                        <input className="input text-xs bg-white" {...equipFields.bind('location_in_factory')} placeholder="e.g. Shop Floor 1" />
                      </Field>
                      <Field label="First Use Date / Year">
                        <select className="input text-xs mb-1 bg-white" {...equipFields.bind('first_use_type')}>
                          <option value="NOT_APPLICABLE">Not Applicable</option>
                          <option value="EXACT_DATE">Exact Date (DD/MM/YYYY)</option>
                          <option value="YEAR_ONLY">Year Only (YYYY)</option>
                          <option value="NOT_KNOWN">Not Known</option>
                        </select>
                        {equipFields.values.first_use_type === 'EXACT_DATE' && (
                          <input className="input text-xs bg-white" type="date" {...equipFields.bind('first_use_value')} />
                        )}
                        {equipFields.values.first_use_type === 'YEAR_ONLY' && (
                          <select className="input text-xs bg-white" {...equipFields.bind('first_use_value')}>
                            <option value="">Select year…</option>
                            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                          </select>
                        )}
                      </Field>
                    </div>

                    {equipRule && (
                      <div className="rounded bg-brand-100/60 p-2.5 border border-brand-200 text-xs space-y-1">
                        <p className="font-semibold text-brand-800 uppercase text-[10px]">Test Sentence Preview</p>
                        <p className="text-brand-900">{equipPreviewSentence}</p>
                        {equipPreviewLoad && <p className="text-brand-700 text-[10px]">Calculated Load: {equipPreviewLoad}</p>}
                      </div>
                    )}
                  </div>
                )}

                {/* When Editing existing Form 10: Show read-only equipment selector */}
                {editing && (
                  <Field label="Equipment" required>
                    <SearchableSelect
                      options={companyEquipment.map((e) => ({
                        value: e.id,
                        label: e.equipment_short_code || e.internal_id_number,
                      }))}
                      value={fields.values.equipment_id}
                      onChange={(v) => fields.setField('equipment_id', v)}
                      placeholder="Select equipment…"
                    />
                  </Field>
                )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Field
                    label="Competent Person"
                    required
                    hint={
                      <button
                        type="button"
                        onClick={() => setShowPersonnelForm((v) => !v)}
                        className="text-xs text-brand-600 hover:text-brand-700 font-medium inline-flex items-center gap-1 cursor-pointer"
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {showPersonnelForm ? 'Choose existing' : 'New Personnel'}
                      </button>
                    }
                  >
                    <AsyncSearchableSelect
                      value={fields.values.personnel_id}
                      onChange={(v) => fields.setField('personnel_id', v)}
                      fetchOptions={fetchCompetentPersonOptions}
                      placeholder="Search inspector…"
                      disabled={showPersonnelForm}
                    />
                    {selectedPersonnel && !showPersonnelForm && (
                      <>
                        {personnelLicenceStatus.isExpired && (
                          <div className="mt-2 rounded-lg bg-red-50 border border-red-200 p-2.5 text-xs text-red-700 flex items-start gap-2 shadow-2xs">
                            <span className="text-base leading-none">🔴</span>
                            <div>
                              <p className="font-bold text-red-800">Licence Expired — Form 10 Creation Blocked</p>
                              <p className="mt-0.5 text-red-700 leading-relaxed">
                                {selectedPersonnel.full_name}'s licence expired on <span className="font-semibold">{personnelLicenceStatus.formattedDate}</span>.
                                Please renew their licence or select an active Competent Person.
                              </p>
                            </div>
                          </div>
                        )}
                        {personnelLicenceStatus.isExpiringSoon && (
                          <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900 flex items-start gap-2 shadow-2xs">
                            <span className="text-base leading-none">⚠️</span>
                            <div>
                              <p className="font-bold text-amber-900">Licence Expiring Soon ({personnelLicenceStatus.daysRemaining} days remaining)</p>
                              <p className="mt-0.5 text-amber-800 leading-relaxed">
                                {selectedPersonnel.full_name}'s licence will expire on <span className="font-semibold">{personnelLicenceStatus.formattedDate}</span>.
                              </p>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </Field>
                </div>
                <div>
                  <Field label="Examination Date" required>
                    <input className="input" type="date" {...fields.bind('examination_date')} required />
                  </Field>
                </div>
              </div>

              {/* Inline Personnel Creation Form */}
              {showPersonnelForm && (
                <InlinePersonnelForm
                  initialRole="competent_person"
                  onCreated={(created) => {
                    fields.setField('personnel_id', created.id);
                    setShowPersonnelForm(false);
                  }}
                  onCancel={() => setShowPersonnelForm(false)}
                />
              )}

              {/* Test Method (Point 5) */}
              <div>
                <Field
                  label="Test Method (Point 5)"
                  required
                  hint={
                    <button
                      type="button"
                      onClick={() => setShowInlineTestMethodForm((v) => !v)}
                      className="text-xs text-brand-600 hover:text-brand-700 font-medium inline-flex items-center gap-1 cursor-pointer"
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {showInlineTestMethodForm ? 'Hide Form ✕' : 'New Method'}
                    </button>
                  }
                >
                  <select className="input text-xs" {...fields.bind('test_method')}>
                    {allTestMethodOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </Field>
                {showInlineTestMethodForm && (
                  <InlineTestMethodForm
                    onCreated={(created) => {
                      setCustomTestMethods((prev) => [...prev, created]);
                      fields.setField('test_method', created.name);
                      setShowInlineTestMethodForm(false);
                    }}
                    onCancel={() => setShowInlineTestMethodForm(false)}
                  />
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Field label="Defect Status" required>
                    <select className="input" {...fields.bind('defect_status')}>
                      <option value="SAFE">Safe</option>
                      <option value="REJECT">Reject</option>
                      <option value="NOT_APPLICABLE">Not Applicable</option>
                    </select>
                  </Field>
                </div>
                <div>
                  <Field label="Annealing Details (Point 7)">
                    <input className="input" {...fields.bind('annealing_details')} />
                  </Field>
                </div>
              </div>

              <div>
                <Field label="Previous Inspection Details (Point 6)">
                  <input className="input" {...fields.bind('previous_inspection_details')} placeholder="Last Inspection done on DD/MM/YYYY by..." />
                </Field>
              </div>

              {fields.values.defect_status !== 'SAFE' && fields.values.defect_status !== 'NOT_APPLICABLE' && (
                <div>
                  <Field label="Defect Remarks (Point 8)">
                    <textarea
                      className="input"
                      rows={2}
                      {...fields.bind('defect_remarks')}
                      placeholder="Describe reason for reject / defect & remedial steps"
                    />
                  </Field>
                </div>
              )}

              {modalError && <ErrorBanner message={modalError} onDismiss={() => setModalError('')} />}

              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-gray-200">
                {isCompanyIncomplete ? (
                  <p className="text-xs text-amber-700 font-medium flex items-center gap-1">
                    <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    Save company details above to enable Form 10 creation.
                  </p>
                ) : <div />}
                <div className="flex gap-2 shrink-0">
                  <button type="button" onClick={() => { setModalOpen(false); setIsResumed(false); }} className="btn btn-secondary text-sm h-9 px-4 whitespace-nowrap">Cancel</button>
                  <button type="submit" disabled={saving || isCompanyIncomplete || !isForm10Valid} className="btn btn-primary font-medium px-5 text-sm h-9 whitespace-nowrap shrink-0 inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                    {saving ? 'Creating Certificate…' : 'Generate & Save Form 10'}
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Right Panel: Live Vector PDF Document Preview */}
          <div className="lg:col-span-7 bg-slate-100 p-4 rounded-xl border border-slate-300 flex flex-col h-full shadow-inner">
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                Live Form 10 PDF Document Preview
              </span>
              <span className="text-xs text-slate-500">Official Rule 60 Format</span>
            </div>

            {livePdfBlobUrl ? (
              <iframe
                src={livePdfBlobUrl}
                className="w-full h-full min-h-[580px] rounded-lg border border-slate-300 shadow bg-white"
                title="Live Form 10 PDF Preview"
              />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-slate-500">
                Generating PDF document preview...
              </div>
            )}
          </div>

        </div>
      </Modal>

      {/* Direct PDF Preview Modal for existing Form 10 Certificate */}
      <Modal
        open={viewModalOpen}
        onClose={closeViewModal}
        title={`Form 10 PDF Document — ${viewCertNumber}`}
        size="xl"
      >
        {viewLoading ? (
          <div className="py-20 flex flex-col items-center justify-center space-y-3">
            <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-brand-600" />
            <p className="text-sm font-medium text-gray-600">Loading Form 10 PDF Document...</p>
          </div>
        ) : pdfBlobUrl ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-slate-50 p-2.5 rounded-lg border border-slate-200">
              <span className="text-xs font-semibold text-gray-700">{viewCertNumber}</span>
              {viewCertId && (
                <button
                  onClick={() => downloadPdf(viewCertId)}
                  className="btn btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Download PDF
                </button>
              )}
            </div>

            <iframe
              src={pdfBlobUrl}
              className="w-full h-[76vh] rounded-lg border border-slate-300 shadow-inner bg-slate-100"
              title={`Form 10 PDF ${viewCertNumber}`}
            />
          </div>
        ) : (
          <div className="py-12 text-center text-red-600 text-sm">
            Failed to load PDF document.
          </div>
        )}
      </Modal>

      <BulkForm10Modal
        open={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        initialCompanyId={fields.values.company_id}
        onSuccess={() => {
          load();
        }}
        allTypeOptions={allTypeOptions}
        fetchCompanyOptions={fetchCompanyOptions}
        fetchPersonnelOptions={fetchCompetentPersonOptions}
      />

      <Form10ExcelImportModal
        open={excelImportModalOpen}
        onClose={() => setExcelImportModalOpen(false)}
        onSuccess={() => {
          load();
        }}
      />

      <Form10ExportIndexModal
        open={exportIndexModalOpen}
        onClose={() => setExportIndexModalOpen(false)}
        initialCompanyId={companyFilter}
      />

      <ConfirmDialog
        open={!!deleteId}
        title="Move Certificate to Trash"
        message="Are you sure you want to delete this certificate? It will be moved to Trash and can be restored within 30 days."
        confirmLabel="Move to Trash"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />

      {isWrite && (
        <BulkActionBar
          selectedCount={selectedCount}
          entityName="certificates"
          onClearSelection={clearSelection}
          onDelete={() => setBulkDeleteDialogOpen(true)}
          isDeleting={bulkDeleting}
        />
      )}

      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        title={`Move ${selectedCount} Certificates to Trash`}
        message={`Are you sure you want to delete ${selectedCount} selected Form 10 certificates? They will be moved to Trash and can be restored within 30 days.`}
        confirmLabel={`Move ${selectedCount} to Trash`}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteDialogOpen(false)}
      />

    </div>
  );
}

function BulkForm10Modal({
  open,
  onClose,
  initialCompanyId,
  onSuccess,
  allTypeOptions: _allTypeOptions,
  fetchCompanyOptions,
  fetchPersonnelOptions: _fetchPersonnelOptions,
}: {
  open: boolean;
  onClose: () => void;
  initialCompanyId?: string;
  onSuccess: () => void;
  allTypeOptions: Array<{ value: string; label: string }>;
  fetchCompanyOptions: (query: string) => Promise<Array<{ value: string; label: string }>>;
  fetchPersonnelOptions?: (query: string) => Promise<Array<{ value: string; label: string }>>;
}) {
  const [companyId, setCompanyId] = useState(initialCompanyId || '');
  const [equipmentType, setEquipmentType] = useState('');
  const [quantity, setQuantity] = useState(10);
  const [tagPrefix, setTagPrefix] = useState('');
  const [personnelId, setPersonnelId] = useState('');
  const [examDate, setExamDate] = useState(new Date().toISOString().split('T')[0]);
  const [testMethod, setTestMethod] = useState<string>(DEFAULT_TEST_METHOD);
  const [customTestMethods, setCustomTestMethods] = useState<Array<{ id: string; name: string }>>([]);
  const [showInlineTestMethodForm, setShowInlineTestMethodForm] = useState(false);
  const [defectStatus, setDefectStatus] = useState('SAFE');
  const [defectRemarks, setDefectRemarks] = useState('');
  const [annealingDetails, setAnnealingDetails] = useState('');
  const [prevInspection, setPrevInspection] = useState('');
  const [swlValue, setSwlValue] = useState('');
  const [liftHeight, setLiftHeight] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [showInlineTypeForm, setShowInlineTypeForm] = useState(false);
  const [customTypes, setCustomTypes] = useState<Array<{ key: string; label: string; short_code?: string }>>([]);
  const [showCompanyForm, setShowCompanyForm] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selectedBulkCompany, setSelectedBulkCompany] = useState<Record<string, string> | null>(null);
  const [showBulkEditCompanyForm, setShowBulkEditCompanyForm] = useState(false);
  const [updatingBulkCompany, setUpdatingBulkCompany] = useState(false);

  useEffect(() => {
    api.testMethods.list()
      .then((res) => {
        if (res.data) setCustomTestMethods(res.data);
      })
      .catch(() => {});
  }, []);

  const allTestMethodOptions = useMemo(() => {
    return buildTestMethodOptions(customTestMethods);
  }, [customTestMethods]);

  // Fetch company details when companyId changes
  useEffect(() => {
    if (!companyId) { setSelectedBulkCompany(null); return; }
    api.companies.list({ search: '', limit: 200 }).then((res) => {
      const all = (res.data ?? []) as Record<string, string>[];
      const found = all.find((c) => c.id === companyId);
      setSelectedBulkCompany(found ?? null);
    }).catch(() => setSelectedBulkCompany(null));
  }, [companyId]);

  const isBulkCompanyIncomplete = useMemo(() => {
    if (!selectedBulkCompany) return false;
    const c = selectedBulkCompany;
    const email = c.contact_email?.trim() ?? '';
    const emails = email.split(/[,;\s]+/).filter(Boolean);
    const isEmailDummy = emails.length === 0 || emails.every((e) => e === 'info@company.com' || e === 'contact@company.com');
    return (
      !c.company_name?.trim() ||
      !c.short_code?.trim() ||
      !c.occupier_name?.trim() || c.occupier_name?.trim() === 'N/A' ||
      !c.factory_address?.trim() || c.factory_address?.trim() === 'N/A' ||
      !c.license_number?.trim() ||
      !c.registration_number?.trim() ||
      isEmailDummy
    );
  }, [selectedBulkCompany]);

  const editBulkCompanyFields = useFormFields({
    short_code: '', company_name: '', occupier_name: '',
    factory_address: '', license_number: '', license_expiry_date: '', registration_number: '',
    nic_code: '', contact_email: '', communication_email: '', contact_person: '', mobile_number: '',
  });

  useEffect(() => {
    if (selectedBulkCompany) {
      const email = selectedBulkCompany.contact_email?.trim() ?? '';
      const cleanEmail = (email === 'info@company.com' || email === 'contact@company.com') ? '' : email;
      editBulkCompanyFields.patchValues({
        short_code: selectedBulkCompany.short_code ?? '',
        company_name: selectedBulkCompany.company_name ?? '',
        occupier_name: selectedBulkCompany.occupier_name === 'N/A' ? '' : (selectedBulkCompany.occupier_name ?? ''),
        factory_address: selectedBulkCompany.factory_address === 'N/A' ? '' : (selectedBulkCompany.factory_address ?? ''),
        license_number: selectedBulkCompany.license_number ?? '',
        license_expiry_date: selectedBulkCompany.license_expiry_date ?? '',
        registration_number: selectedBulkCompany.registration_number ?? '',
        nic_code: selectedBulkCompany.nic_code ?? '',
        contact_email: cleanEmail,
        communication_email: selectedBulkCompany.communication_email ?? '',
        contact_person: selectedBulkCompany.contact_person ?? '',
        mobile_number: selectedBulkCompany.mobile_number ?? '',
      });
    }
  }, [selectedBulkCompany]);

  const [checkingBulkEditShortCode, setCheckingBulkEditShortCode] = useState(false);
  const [bulkEditShortCodeStatus, setBulkEditShortCodeStatus] = useState<{ available?: boolean; message?: string; suggested?: string } | null>(null);

  useDebounce(editBulkCompanyFields.values.short_code, async () => {
    const code = editBulkCompanyFields.values.short_code.trim();
    if (!code || !selectedBulkCompany) {
      setBulkEditShortCodeStatus(null);
      return;
    }
    setCheckingBulkEditShortCode(true);
    try {
      const res = await api.companies.checkShortCode(code, editBulkCompanyFields.values.company_name, selectedBulkCompany.id);
      if (res.data) setBulkEditShortCodeStatus(res.data);
    } catch {
      // ignore
    } finally {
      setCheckingBulkEditShortCode(false);
    }
  });

  const isBulkEditCompanyValid = useMemo(() => {
    const v = editBulkCompanyFields.values;
    return (
      !!v.company_name.trim() &&
      !!v.short_code.trim() &&
      !!v.occupier_name.trim() && v.occupier_name.trim() !== 'N/A' &&
      !!v.factory_address.trim() && v.factory_address.trim() !== 'N/A' &&
      !!v.license_number.trim() &&
      !!v.registration_number.trim() &&
      validateMultipleEmails(v.contact_email) &&
      validateOptionalMultipleEmails(v.communication_email) &&
      (bulkEditShortCodeStatus?.available !== false)
    );
  }, [editBulkCompanyFields.values, bulkEditShortCodeStatus]);

  const isBulkForm10Valid = useMemo(() => {
    return (
      !!companyId &&
      !!equipmentType &&
      quantity >= 1 &&
      !!personnelId &&
      !!examDate
    );
  }, [companyId, equipmentType, quantity, personnelId, examDate]);

  const handleBulkSuggestEditShortCode = async () => {
    const name = editBulkCompanyFields.values.company_name.trim();
    if (!name) return;
    try {
      const res = await api.companies.suggestShortCode(name, selectedBulkCompany?.id);
      if (res.data?.short_code) editBulkCompanyFields.setField('short_code', res.data.short_code);
    } catch {
      const candidates = generateSmartShortCodeCandidates(name);
      if (candidates.length > 0) editBulkCompanyFields.setField('short_code', candidates[0]);
    }
  };

  const handleBulkUpdateCompanyDetails = async () => {
    if (!selectedBulkCompany) return;
    const missing: string[] = [];
    if (!editBulkCompanyFields.values.company_name.trim()) missing.push('Company Name');
    if (!editBulkCompanyFields.values.short_code.trim()) missing.push('Short Code');
    if (!editBulkCompanyFields.values.occupier_name.trim()) missing.push('Occupier Name');
    if (!editBulkCompanyFields.values.factory_address.trim()) missing.push('Factory Address');
    if (!editBulkCompanyFields.values.license_number.trim()) missing.push('License Number');
    if (!editBulkCompanyFields.values.registration_number.trim()) missing.push('Registration Number');
    if (!editBulkCompanyFields.values.contact_email.trim()) missing.push('Contact Email');

    if (missing.length) {
      setError(`Please fill in all mandatory company fields: ${missing.join(', ')}.`);
      return;
    }

    const emailVal = editBulkCompanyFields.values.contact_email.trim();
    if (!validateMultipleEmails(emailVal)) {
      setError('Please enter valid Contact Email address(es), separated by commas.');
      return;
    }

    if (!validateOptionalMultipleEmails(editBulkCompanyFields.values.communication_email)) {
      setError('Please enter valid Communication Email address(es), separated by commas.');
      return;
    }

    if (bulkEditShortCodeStatus && !bulkEditShortCodeStatus.available) {
      setError(`Cannot save: Short code '${editBulkCompanyFields.values.short_code.toUpperCase()}' is already taken.`);
      return;
    }

    setUpdatingBulkCompany(true);
    try {
      const payload = {
        ...editBulkCompanyFields.values,
        contact_email: formatMultipleEmails(emailVal) || '',
        communication_email: formatMultipleEmails(editBulkCompanyFields.values.communication_email) || '',
      };
      const updatedRes = await api.companies.update(selectedBulkCompany.id, payload);
      const updatedComp = (updatedRes as { data?: Record<string, string> }).data;
      if (updatedComp) {
        setSelectedBulkCompany(updatedComp);
        setShowBulkEditCompanyForm(false);
      }
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update company details');
    } finally {
      setUpdatingBulkCompany(false);
    }
  };

  const mergedTypeOptions = useMemo(() => {
    return buildEquipmentTypeOptions(customTypes);
  }, [customTypes]);

  useEffect(() => {
    if (initialCompanyId) setCompanyId(initialCompanyId);
  }, [initialCompanyId]);

  const handleSuggestPrefix = () => {
    if (!companyId || !equipmentType) return;
    api.equipment.suggestTagName(companyId, equipmentType)
      .then((res) => {
        if (res.data?.prefix) setTagPrefix(res.data.prefix);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!companyId || !equipmentType) return;
    handleSuggestPrefix();
  }, [companyId, equipmentType]);

  const previewTags = useMemo(() => {
    if (!tagPrefix.trim() || !quantity || quantity <= 0) return [];
    const prefix = tagPrefix.trim().toUpperCase();
    const count = Math.min(quantity, 8);
    const tags: string[] = [];
    for (let i = 1; i <= count; i++) {
      tags.push(`${prefix}/${String(i).padStart(2, '0')}`);
    }
    if (quantity > 8) tags.push(`… and ${quantity - 8} more`);
    return tags;
  }, [tagPrefix, quantity]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId) { setError('Please select a Company.'); return; }
    if (!equipmentType) { setError('Please select an Equipment Type.'); return; }
    if (!personnelId) { setError('Please select a Competent Person.'); return; }

    const confirmMsg = `Confirm Bulk Action:\n\nThis will create ${quantity} Equipment items (Tags: ${previewTags.slice(0, 3).join(', ')}...) AND generate ${quantity} corresponding Form 10 certificates.\n\nDo you want to proceed?`;
    if (!window.confirm(confirmMsg)) return;

    setSaving(true);
    setError('');

    try {
      // 1. Bulk create equipment
      const bulkRes = await api.equipment.bulkCreate({
        company_id: companyId,
        equipment_name_type: equipmentType,
        quantity,
        tag_prefix: tagPrefix.trim() || undefined,
        swl_value: swlValue ? parseFloat(swlValue) : undefined,
        lift_height: liftHeight.trim() || undefined,
        make: make.trim() || undefined,
        model: model.trim() || undefined,
      });

      const eqIds = bulkRes.data?.items ?? [];
      if (!eqIds || eqIds.length === 0) {
        throw new Error('Failed to bulk create equipment items.');
      }

      // 2. Create Form 10 certificates for each equipment ID
      for (const eqId of eqIds) {
        await api.form10.create({
          company_id: companyId,
          equipment_id: eqId,
          personnel_id: personnelId,
          examination_date: examDate,
          test_method: testMethod,
          defect_status: defectStatus,
          defect_remarks: (defectStatus === 'SAFE' || defectStatus === 'NOT_APPLICABLE') ? undefined : (defectRemarks.trim() || undefined),
          annealing_details: annealingDetails.trim() || undefined,
          previous_inspection_details: prevInspection.trim() || undefined,
        });
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to bulk create Equipment & Form 10 certificates');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="⚡ Bulk Generate Equipment & Form 10 Certificates" size="xl">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg flex justify-between items-center">
            <span>{error}</span>
            <button type="button" onClick={() => setError('')}>✕</button>
          </div>
        )}

        <div className="bg-indigo-50 p-3 rounded-xl border border-indigo-200 text-xs text-indigo-900 space-y-1">
          <p className="font-bold flex items-center gap-1.5 text-indigo-800">
            <span>⚡ Bulk Equipment & Form 10 Generator</span>
          </p>
          <p className="text-indigo-700">
            Ideal when inspecting 10, 50, or 100+ identical equipment items at once.
            This will auto-generate all equipment items with incremented Tag Names AND create matching Form 10 certificates for all of them.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Field
              label="Company"
              required
              hint={
                <button
                  type="button"
                  onClick={() => setShowCompanyForm(!showCompanyForm)}
                  className="text-[11px] text-emerald-700 font-bold hover:underline cursor-pointer"
                >
                  + New Company
                </button>
              }
            >
              <AsyncSearchableSelect
                placeholder="Search company…"
                fetchOptions={fetchCompanyOptions}
                value={companyId}
                onChange={setCompanyId}
                isCompanySelect={true}
              />
            </Field>

            {/* Company Incomplete Warning with Collapsible Edit Form - Directly Below Company Dropdown */}
            {selectedBulkCompany && isBulkCompanyIncomplete && !showCompanyForm && (
              <div className="mt-2.5 rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-amber-900 font-semibold text-xs">
                    <svg className="w-4 h-4 text-amber-600 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>Note: Company details incomplete ({selectedBulkCompany.company_name})</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowBulkEditCompanyForm((v) => !v)}
                    className="text-xs font-semibold text-amber-800 hover:text-amber-900 underline cursor-pointer shrink-0 whitespace-nowrap inline-flex items-center gap-1"
                  >
                    {showBulkEditCompanyForm ? 'Hide Form ✕' : <span className="whitespace-nowrap">Complete Details ✏️</span>}
                  </button>
                </div>

                {showBulkEditCompanyForm && (
                  <div className="pt-2 border-t border-amber-200/80 space-y-3">
                    <p className="text-xs text-amber-800">
                      The selected company <strong>{selectedBulkCompany.company_name}</strong> is missing mandatory information (License No, Reg No, Occupier Name, or Factory Address). Complete and save these details below before generating Form 10.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                      <Field label="Short Code" required hint={<span className="text-[11px] text-brand-600 font-medium">Must be unique</span>}>
                        <div className="space-y-1">
                          <div className="flex gap-1.5">
                            <input className="input text-xs bg-white font-mono uppercase" {...editBulkCompanyFields.bind('short_code')} placeholder="Short Code" />
                            <button
                              type="button"
                              onClick={handleBulkSuggestEditShortCode}
                              title="Suggest available unique short code"
                              className="px-2 py-1 text-xs font-semibold bg-brand-50 text-brand-700 hover:bg-brand-100 border border-brand-200/80 rounded-lg shrink-0 cursor-pointer"
                            >
                              Suggest
                            </button>
                          </div>
                          {checkingBulkEditShortCode && (
                            <p className="text-[11px] text-blue-600 font-medium flex items-center gap-1">
                              <span className="animate-spin h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full" />
                              Checking availability…
                            </p>
                          )}
                          {!checkingBulkEditShortCode && bulkEditShortCodeStatus && editBulkCompanyFields.values.short_code.trim() && (
                            bulkEditShortCodeStatus.available ? (
                              <p className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1">
                                ✓ Short code '{editBulkCompanyFields.values.short_code.toUpperCase()}' is available
                              </p>
                            ) : (
                              <div className="text-[11px] text-red-600 font-semibold space-y-0.5">
                                <p>✕ {bulkEditShortCodeStatus.message}</p>
                                {bulkEditShortCodeStatus.suggested && (
                                  <p>
                                    Suggested code:{' '}
                                    <button
                                      type="button"
                                      onClick={() => editBulkCompanyFields.setField('short_code', bulkEditShortCodeStatus.suggested!)}
                                      className="text-brand-700 underline font-bold hover:text-brand-900 cursor-pointer"
                                    >
                                      Use '{bulkEditShortCodeStatus.suggested}'
                                    </button>
                                  </p>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </Field>
                      <Field label="Company Name" required>
                        <input className="input text-xs bg-white" {...editBulkCompanyFields.bind('company_name')} placeholder="Company Name" />
                      </Field>
                      <Field label="Occupier Name" required>
                        <input className="input text-xs bg-white" {...editBulkCompanyFields.bind('occupier_name')} placeholder="Occupier Name" />
                      </Field>
                      <Field label="Factory Address" required>
                        <input className="input text-xs bg-white" {...editBulkCompanyFields.bind('factory_address')} placeholder="Factory Address" />
                      </Field>
                      <Field label="License No" required>
                        <input className="input text-xs bg-white" {...editBulkCompanyFields.bind('license_number')} placeholder="License No" />
                      </Field>
                      <Field label="License Expiry Date">
                        <input className="input text-xs bg-white" type="date" {...editBulkCompanyFields.bind('license_expiry_date')} />
                      </Field>
                      <Field label="Reg No" required>
                        <input className="input text-xs bg-white" {...editBulkCompanyFields.bind('registration_number')} placeholder="Reg No" />
                      </Field>
                      <Field label="Contact Email" hint="Comma-separated for multiple" required>
                        <input className="input text-xs bg-white" type="text" {...editBulkCompanyFields.bind('contact_email')} placeholder="e.g. contact@company.com, admin@company.com" autoComplete="off" required />
                      </Field>
                      <Field label="Communication Email" hint="Optional, comma-separated for multiple">
                        <input className="input text-xs bg-white" type="text" {...editBulkCompanyFields.bind('communication_email')} placeholder="e.g. comms@company.com" autoComplete="off" />
                      </Field>
                    </div>
                    <div className="flex justify-end pt-1">
                      <button
                        type="button"
                        onClick={handleBulkUpdateCompanyDetails}
                        disabled={updatingBulkCompany || !isBulkEditCompanyValid}
                        className="btn btn-primary text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {updatingBulkCompany ? 'Saving Company Details…' : 'Save & Update Company Details'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <Field
              label="Equipment Type"
              required
              hint={
                <button
                  type="button"
                  onClick={() => setShowInlineTypeForm(!showInlineTypeForm)}
                  className="text-[11px] text-brand-600 font-bold hover:underline cursor-pointer"
                >
                  + New Type
                </button>
              }
            >
              <SearchableSelect
                placeholder="Select type…"
                options={mergedTypeOptions}
                value={equipmentType}
                onChange={setEquipmentType}
              />
              {showInlineTypeForm && (
                <InlineEquipmentTypeForm
                  onCreated={(newType) => {
                    setCustomTypes((prev) => [...prev, newType]);
                    setEquipmentType(newType.key);
                    setShowInlineTypeForm(false);
                  }}
                  onCancel={() => setShowInlineTypeForm(false)}
                />
              )}
            </Field>
          </div>
          <div>
            <Field label="Quantity (Items)" required>
              <input
                type="number"
                min="1"
                max="500"
                className="input font-semibold"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 1)}
                required
              />
            </Field>
          </div>
        </div>

        {/* Inline Company Creation Form */}
        {showCompanyForm && (
          <InlineCompanyForm
            onCreated={(created) => {
              setCompanyId(created.id);
              setShowCompanyForm(false);
            }}
            onCancel={() => setShowCompanyForm(false)}
          />
        )}

        {/* Tag Name Prefix & Live Preview */}
        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Field label="Tag Name Prefix">
              <div className="flex gap-2">
                <input
                  type="text"
                  className="input font-mono uppercase font-semibold flex-1"
                  placeholder="e.g. SPS/CPB"
                  value={tagPrefix}
                  onChange={(e) => setTagPrefix(e.target.value.toUpperCase())}
                />
                <button
                  type="button"
                  onClick={handleSuggestPrefix}
                  title="Suggest available prefix"
                  className="px-2.5 py-1 text-xs font-semibold bg-white text-indigo-700 hover:bg-indigo-100 border border-indigo-300 rounded-lg shrink-0 cursor-pointer"
                >
                  Suggest
                </button>
              </div>
            </Field>
          </div>
          <div>
            <label className="label text-xs font-semibold text-gray-700 mb-1">Generated Tag Names Preview</label>
            <div className="flex flex-wrap gap-1.5 p-2 bg-white rounded border border-gray-200 min-h-[38px] items-center">
              {previewTags.length === 0 ? (
                <span className="text-xs text-gray-400">Select company & type to preview tags</span>
              ) : (
                previewTags.map((t, idx) => (
                  <span key={idx} className="px-2 py-0.5 text-xs font-mono bg-indigo-50 text-indigo-700 border border-indigo-200 rounded font-semibold">
                    {t}
                  </span>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Shared Specs */}
        <div className="border-t border-gray-200 pt-3">
          <p className="text-xs font-bold text-gray-800 mb-2">Shared Equipment Specifications</p>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Field label="SWL (Ton)">
              <input className="input text-xs" type="number" step="any" value={swlValue} onChange={(e) => setSwlValue(e.target.value)} placeholder="e.g. 5.0" />
            </Field>
            <Field label="Lift Height">
              <input className="input text-xs" value={liftHeight} onChange={(e) => setLiftHeight(e.target.value)} placeholder="e.g. 10m" />
            </Field>
            <Field label="Make">
              <input className="input text-xs" value={make} onChange={(e) => setMake(e.target.value)} placeholder="e.g. Indef" />
            </Field>
            <Field label="Model">
              <input className="input text-xs" value={model} onChange={(e) => setModel(e.target.value)} placeholder="e.g. M-1" />
            </Field>
          </div>
        </div>

        {/* Shared Certificate Details */}
        <div className="border-t border-gray-200 pt-3 space-y-3">
          <p className="text-xs font-bold text-gray-800">Shared Certificate Details (Form 10)</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Competent Person" required>
              <AsyncSearchableSelect
                value={personnelId}
                onChange={setPersonnelId}
                fetchOptions={fetchCompetentPersonOptions}
                placeholder="Search inspector…"
              />
            </Field>
            <Field label="Examination Date" required>
              <input className="input text-xs" type="date" value={examDate} onChange={(e) => setExamDate(e.target.value)} required />
            </Field>
            <Field label="Defect Status" required>
              <select className="input text-xs" value={defectStatus} onChange={(e) => setDefectStatus(e.target.value)}>
                <option value="SAFE">Safe</option>
                <option value="REJECT">Reject</option>
                <option value="NOT_APPLICABLE">Not Applicable</option>
              </select>
            </Field>
          </div>

          {/* Test Method (Point 5) in Bulk Modal */}
          <div>
            <Field
              label="Test Method (Point 5)"
              required
              hint={
                <button
                  type="button"
                  onClick={() => setShowInlineTestMethodForm((v) => !v)}
                  className="text-xs text-brand-600 hover:text-brand-700 font-medium inline-flex items-center gap-1 cursor-pointer"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {showInlineTestMethodForm ? 'Hide Form ✕' : 'New Method'}
                </button>
              }
            >
              <select className="input text-xs" value={testMethod} onChange={(e) => setTestMethod(e.target.value)}>
                {allTestMethodOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Field>
            {showInlineTestMethodForm && (
              <InlineTestMethodForm
                onCreated={(created) => {
                  setCustomTestMethods((prev) => [...prev, created]);
                  setTestMethod(created.name);
                  setShowInlineTestMethodForm(false);
                }}
                onCancel={() => setShowInlineTestMethodForm(false)}
              />
            )}
          </div>

          <div className={`grid grid-cols-1 ${defectStatus !== 'SAFE' && defectStatus !== 'NOT_APPLICABLE' ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-3`}>
            {defectStatus !== 'SAFE' && defectStatus !== 'NOT_APPLICABLE' && (
              <Field label="Defect Remarks (Point 8)">
                <textarea
                  className="input text-xs"
                  rows={2}
                  value={defectRemarks}
                  onChange={(e) => setDefectRemarks(e.target.value)}
                  placeholder="Describe reason for reject / defect & remedial steps"
                />
              </Field>
            )}
            <Field label="Annealing Details (Point 7)">
              <textarea className="input text-xs" rows={2} value={annealingDetails} onChange={(e) => setAnnealingDetails(e.target.value)} placeholder="Heat treatment / annealing details..." />
            </Field>
            <Field label="Previous Inspection Details (Point 6)">
              <textarea className="input text-xs" rows={2} value={prevInspection} onChange={(e) => setPrevInspection(e.target.value)} placeholder="Last inspection details..." />
            </Field>
          </div>
        </div>

        {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

        <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
          <button type="button" onClick={onClose} className="btn btn-secondary text-sm h-9 whitespace-nowrap shrink-0">Cancel</button>
          <button type="submit" disabled={saving || isBulkCompanyIncomplete || !isBulkForm10Valid} className="btn bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-5 text-sm cursor-pointer h-9 whitespace-nowrap shrink-0 inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? `Bulk Generating (${quantity} Certificates)…` : `⚡ Confirm & Bulk Create (${quantity} Items + ${quantity} Form 10s)`}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Field({ label, required, hint, children }: { label: string; required?: boolean; hint?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="label text-xs font-semibold text-gray-700 mb-0">{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
        {hint}
      </div>
      {children}
    </div>
  );
}

function ErrorBanner({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between"><span>{message}</span><button onClick={onDismiss} className="text-red-500">✕</button></div>;
}
function isOverdue(dateStr: string): boolean {
  if (!dateStr) return false;
  try {
    const clean = dateStr.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(clean)) {
      const [y, m, d] = clean.split('-').map(Number);
      return new Date(y, m - 1, d) < new Date();
    }
    if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(clean)) {
      const [d, m, y] = clean.split('-').map(Number);
      return new Date(y, m - 1, d) < new Date();
    }
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(clean)) {
      const [d, m, y] = clean.split('/').map(Number);
      return new Date(y, m - 1, d) < new Date();
    }
    return new Date(clean) < new Date();
  } catch {
    return false;
  }
}

function fyOptions(): string[] {
  const now = new Date();
  const currentFY = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return Array.from({ length: 4 }, (_, i) => String(currentFY - i));
}
